import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { UploadCloud, CheckCircle2, Loader2, Ticket, AlertCircle, CalendarDays, MapPin, Clock, Phone, ArrowLeft } from "lucide-react";
import { ParallaxBackground } from "@/components/site/ParallaxBackground";
import { Header } from "@/components/site/Header";
import { formatApiError, labelFor } from "@/lib/errors";
import { useRegPrices } from "@/lib/useRegPrices";

const API = process.env.REACT_APP_BACKEND_URL;
const POSTER = "https://customer-assets-jai6qajn.emergentagent.net/job_pahlawan-tickets/artifacts/jz2z3x7e_amateur%20poster%201.webp";
const PHOTO_EXAMPLE = "https://customer-assets-jai6qajn.emergentagent.net/job_pahlawan-tickets/artifacts/t8teg48m_image.png";
const FEE_INCLUDES = ["Championship Ticket", "Event Jersey", "Singlet", "Medal", "Certificate", "Lucky Draw"];

const OPTIONS = {
  nationality: ["Malaysian", "Others"],
  gender: ["Male", "Female"],
  age_category: [
    "U8 (6-7 y/o)", "U10 (8-9 y/o)", "U12 (10-11 y/o)", "U14 (12-13 y/o)",
    "U16 (14-15 y/o)", "U18 (16-17 y/o)", "SENIOR (18-40 y/o)",
  ],
  weight_class: [...Array.from({ length: 35 }, (_, i) => `${20 + i * 2}KG`), "90KG++"],
  jersey_size: ["XS", "S", "M", "L", "XL", "2XL", "3XL"],
};

const INITIAL = {
  nationality: "", full_name: "", nric_passport: "", gender: "", age_category: "",
  weight_class: "", height_cm: "", jersey_size: "", club_gym: "", coach_name: "",
  coach_phone: "", fight_record: "", email: "",
};

const inputCls =
  "w-full rounded-xl border border-white/15 bg-void/60 px-4 py-3 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan transition-colors";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-cap mb-2 block text-[10px] text-neon-cyan">{label}</span>
    {children}
  </label>
);

const Select = ({ value, onChange, options, placeholder, testid }) => (
  <select
    value={value}
    onChange={onChange}
    className={`${inputCls} appearance-none ${value ? "text-white" : "text-slate-500"}`}
    data-testid={testid}
  >
    <option value="" disabled>{placeholder}</option>
    {options.map((o) => (
      <option key={o} value={o} className="bg-void text-white">{o}</option>
    ))}
  </select>
);

const Section = ({ num, title, children }) => (
  <div className="rounded-3xl border border-white/10 bg-surface/80 p-6 sm:p-8">
    <div className="mb-5 flex items-baseline gap-3">
      <span className="font-display text-2xl text-neon-pink/60">{num}</span>
      <h2 className="font-display text-xl text-white">{title}</h2>
    </div>
    <div className="grid gap-4 sm:grid-cols-2">{children}</div>
  </div>
);

export default function RegisterPage() {
  const [form, setForm] = useState(INITIAL);
  const [photo, setPhoto] = useState({ path: "", preview: "", progress: 0, uploading: false, name: "" });
  const [error, setError] = useState("");
  const [resume, setResume] = useState(null);

  useEffect(() => {
    if (error || resume) (document.querySelector('[data-testid="reg-error"]') || document.querySelector('[data-testid="resume-payment"]'))?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [error, resume]);
  const [submitting, setSubmitting] = useState(false);
  const [slots, setSlots] = useState(null);
  const prices = useRegPrices();
  const fileRef = useRef(null);
  const saleOpen = Date.now() >= new Date("2026-09-22T00:00:00+08:00").getTime();

  useEffect(() => {
    fetch(`${API}/api/registrations/availability`)
      .then((r) => r.json())
      .then(setSlots)
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadPhoto = (file) => {
    if (!file) return;
    setError("");
    setPhoto({ path: "", preview: URL.createObjectURL(file), progress: 0, uploading: true, name: file.name });
    const fd = new FormData();
    fd.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/api/registrations/photo`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setPhoto((p) => ({ ...p, progress: Math.round((e.loaded / e.total) * 100) }));
      }
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status < 400 && data.path) {
          setPhoto((p) => ({ ...p, path: data.path, uploading: false, progress: 100 }));
        } else {
          setPhoto({ path: "", preview: "", progress: 0, uploading: false, name: "" });
          setError(typeof data.detail === "string" ? data.detail : "Photo upload failed");
        }
      } catch {
        setPhoto({ path: "", preview: "", progress: 0, uploading: false, name: "" });
        setError("Photo upload failed — please try again");
      }
    };
    xhr.onerror = () => {
      setPhoto({ path: "", preview: "", progress: 0, uploading: false, name: "" });
      setError("Photo upload failed — check your connection");
    };
    xhr.send(fd);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResume(null);
    const missing = Object.entries(INITIAL).filter(([k]) => !String(form[k]).trim()).map(([k]) => k);
    if (missing.length) {
      setError("Please complete: " + missing.map(labelFor).join(" · "));
      return;
    }
    if (!photo.path) {
      setError("Please upload your profile photo before payment.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/registrations/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reg_type: "amateur", ...form, height_cm: Number(form.height_cm), photo_path: photo.path }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409 && data.detail?.resume) {
          setResume(data.detail);
          setSubmitting(false);
          return;
        }
        throw new Error(formatApiError(data.detail));
      }
      localStorage.setItem("pahlawan_last_registration", data.registration_id);
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen text-white">
      <ParallaxBackground />
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-24 pt-28 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Link to="/register" className="mb-5 inline-flex items-center gap-2 font-mono-cap text-[10px] text-slate-400 transition-colors hover:text-neon-cyan" data-testid="reg-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> All Registrations
          </Link>
          <div className="overflow-hidden rounded-3xl border border-white/10 shadow-[0_0_80px_rgba(255,0,216,0.12)]">
            <img src={POSTER} alt="Wilayah Pahlawan 3.0 — Amateur Fight" className="w-full" data-testid="reg-poster" />
          </div>

          <p className="font-mono-cap mb-3 mt-8 text-[11px] text-neon-cyan">Wilayah Pahlawan 3.0 : MuaySport (Amateur)</p>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <h1 className="font-display text-4xl leading-[0.95] sm:text-5xl">
              AMATEUR <span className="bg-gradient-to-r from-violet-400 via-cyan-300 to-green-400 bg-clip-text text-transparent">REGISTRATION</span>
            </h1>
            <div className="shrink-0 rounded-2xl border border-neon-pink/40 bg-neon-pink/10 px-5 py-3 text-right">
              <p className="font-mono-cap text-[9px] text-slate-400">Registration Fee</p>
              <p className="font-display text-2xl gradient-text" data-testid="reg-price">RM{prices.amateur}</p>
            </div>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2" data-testid="reg-event-info">
            <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-surface/80 px-4 py-3">
              <CalendarDays className="h-5 w-5 shrink-0 text-neon-cyan" />
              <div>
                <p className="font-mono-cap text-[9px] text-slate-500">Date</p>
                <p className="font-sub text-sm font-bold text-white">26 – 27 November 2026</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-surface/80 px-4 py-3">
              <MapPin className="h-5 w-5 shrink-0 text-neon-pink" />
              <div>
                <p className="font-mono-cap text-[9px] text-slate-500">Venue</p>
                <p className="font-sub text-sm font-bold text-white">Stadium Titiwangsa, Kuala Lumpur</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-surface/80 px-4 py-3">
              <Clock className="h-5 w-5 shrink-0 text-violet-400" />
              <div>
                <p className="font-mono-cap text-[9px] text-slate-500">Official Weigh-In</p>
                <p className="font-sub text-sm font-bold text-white">25 November 2026 · 10:00 AM – 6:00 PM</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-neon-pink/30 bg-neon-pink/5 px-4 py-3" data-testid="reg-slots">
              <Ticket className="h-5 w-5 shrink-0 text-neon-pink" />
              <div>
                <p className="font-mono-cap text-[9px] text-slate-500">Registration Limit</p>
                <p className="font-sub text-sm font-bold text-white">
                  {slots ? `${slots.amateur.slots_left} / ${slots.amateur.limit} slots left` : "400 slots only"}
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-surface/80 px-4 py-3 sm:col-span-2">
              <p className="font-mono-cap text-[9px] text-slate-500">Registration fee includes</p>
              <ul className="mt-2 space-y-2">
                {FEE_INCLUDES.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 font-body text-sm text-slate-200">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-neon-cyan" /> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-3 rounded-2xl border border-neon-pink/40 bg-neon-pink/10 px-4 py-3" data-testid="reg-closing">
            <p className="flex items-center gap-2 font-mono-cap text-[10px] text-neon-pink">
              <Clock className="h-4 w-4 shrink-0" /> Registration closes once the 400-participant limit is reached — register early!
            </p>
            <p className="mt-1.5 font-body text-xs text-slate-400">
              Participant will be paired by age & weight category in accordance with IFMA rules.
            </p>
          </div>

          <p className="mt-4 max-w-xl font-body text-sm text-slate-400">
            Fill in your fighter details below. Registration is confirmed once payment is completed via HitPay
            secure checkout — you will receive your <span className="text-slate-200">registration number by email</span>.
            Bring it with your NRIC/Passport to the Official Weigh-In — you will collect all your items there.
          </p>
          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-neon-cyan/40 bg-neon-cyan/10 px-5 py-4 shadow-[0_0_30px_rgba(0,247,255,0.10)]" data-testid="reg-contact">
            <Phone className="h-5 w-5 shrink-0 text-neon-cyan" />
            <p className="font-body text-base text-slate-200">
              Any questions: <span className="font-bold text-neon-cyan">Admin Pahlawan 013-332 3998</span> · <span className="font-bold text-neon-cyan">Coach EJ 016-715 6357</span>
            </p>
          </div>
        </motion.div>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="reg-form">
          <Section num="01" title="IDENTITY">
            <Select value={form.nationality} onChange={set("nationality")} options={OPTIONS.nationality} placeholder="Nationality — select" testid="reg-nationality" />
            <Select value={form.gender} onChange={set("gender")} options={OPTIONS.gender} placeholder="Gender — select" testid="reg-gender" />
            <div className="sm:col-span-2">
              <Field label="Full Name (as per NRIC/Passport)">
                <input value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="e.g. Muhammad Ali bin Ahmad" data-testid="reg-full-name" />
              </Field>
            </div>
            <Field label="NRIC / Passport Number">
              <input value={form.nric_passport} onChange={set("nric_passport")} className={inputCls} placeholder="e.g. 900101-14-5678" data-testid="reg-nric" />
            </Field>
            <Field label="Email (payment receipt & confirmation)">
              <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@email.com" data-testid="reg-email" />
            </Field>
          </Section>

          <Section num="02" title="FIGHT DETAILS">
            <div>
              <span className="font-mono-cap mb-2 block text-[10px] text-neon-cyan">Age Category (as of 2026)</span>
              <Select value={form.age_category} onChange={set("age_category")} options={OPTIONS.age_category} placeholder="Age — select" testid="reg-age" />
            </div>
            <div>
              <span className="font-mono-cap mb-2 block text-[10px] text-neon-cyan">Weight — select your fight weight</span>
              <Select value={form.weight_class} onChange={set("weight_class")} options={OPTIONS.weight_class} placeholder="Weight (KG) — select" testid="reg-weight" />
            </div>
            <Field label="Height (CM)">
              <input type="number" min="50" max="250" value={form.height_cm} onChange={set("height_cm")} className={inputCls} placeholder="e.g. 170" data-testid="reg-height" />
            </Field>
            <div>
              <span className="font-mono-cap mb-2 block text-[10px] text-neon-cyan">Jersey Size</span>
              <Select value={form.jersey_size} onChange={set("jersey_size")} options={OPTIONS.jersey_size} placeholder="Size — select" testid="reg-jersey" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Fight Record (as stated in fight book)">
                <input value={form.fight_record} onChange={set("fight_record")} className={inputCls} placeholder="e.g. 3 fights - 2 wins - 1 loss" data-testid="reg-record" />
              </Field>
            </div>
          </Section>

          <Section num="03" title="TEAM">
            <div className="sm:col-span-2">
              <Field label="Club / Gym Name">
                <input value={form.club_gym} onChange={set("club_gym")} className={inputCls} placeholder="e.g. Pahlawan Muaythai Gym" data-testid="reg-gym" />
              </Field>
            </div>
            <Field label="Manager / Coach Name">
              <input value={form.coach_name} onChange={set("coach_name")} className={inputCls} placeholder="Coach full name" data-testid="reg-coach" />
            </Field>
            <Field label="Manager / Coach Phone Number">
              <input value={form.coach_phone} onChange={set("coach_phone")} className={inputCls} placeholder="e.g. 012-3456789" data-testid="reg-coach-phone" />
            </Field>
          </Section>

          <div className="rounded-3xl border border-white/10 bg-surface/80 p-6 sm:p-8">
            <div className="mb-5 flex items-baseline gap-3">
              <span className="font-display text-2xl text-neon-pink/60">04</span>
              <h2 className="font-display text-xl text-white">PROFILE PHOTO</h2>
            </div>
            <div className="mb-5 flex items-center gap-4 rounded-2xl border border-neon-cyan/20 bg-neon-cyan/5 p-4">
              <img src={PHOTO_EXAMPLE} alt="Required profile photo example" className="h-24 w-20 shrink-0 rounded-xl border border-white/15 object-cover" data-testid="reg-photo-example" />
              <div>
                <p className="font-mono-cap text-[10px] text-neon-cyan">Reference Example</p>
                <p className="mt-1 font-body text-xs text-slate-400">
                  Clear, high-resolution photo — plain background, face clearly visible, half-body
                  in fight stance like the example shown.
                </p>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="reg-photo-input" />
            {photo.preview ? (
              <div className="flex items-center gap-5">
                <img src={photo.preview} alt="Profile preview" className="h-28 w-28 rounded-2xl border border-white/15 object-cover" data-testid="reg-photo-preview" />
                <div className="flex-1">
                  {photo.uploading ? (
                    <>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-white/10">
                        <div className="h-full rounded-full bg-gradient-to-r from-neon-pink to-neon-violet transition-all" style={{ width: `${photo.progress}%` }} />
                      </div>
                      <p className="mt-2 font-mono-cap text-[10px] text-slate-400">Uploading… {photo.progress}%</p>
                    </>
                  ) : (
                    <p className="flex items-center gap-2 font-mono-cap text-[10px] text-emerald-400" data-testid="reg-photo-ok">
                      <CheckCircle2 className="h-4 w-4" /> Photo uploaded
                    </p>
                  )}
                  <button type="button" onClick={() => fileRef.current?.click()} className="mt-3 rounded-full border border-white/15 px-4 py-2 font-mono-cap text-[10px] text-slate-300 hover:border-neon-cyan" data-testid="reg-photo-change">
                    Change Photo
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex w-full flex-col items-center gap-3 rounded-2xl border border-dashed border-white/20 bg-void/40 px-6 py-10 transition-colors hover:border-neon-cyan/60"
                data-testid="reg-photo-drop"
              >
                <UploadCloud className="h-8 w-8 text-neon-cyan" />
                <span className="font-sub text-sm text-slate-300">Upload a clear, high-resolution photo</span>
                <span className="font-mono-cap text-[9px] text-slate-500">JPG / PNG / WEBP · max 10MB</span>
              </button>
            )}
          </div>

          {resume && (
            <div className="rounded-2xl border border-amber-400/40 bg-amber-400/10 px-5 py-4" data-testid="resume-payment">
              <p className="font-mono-cap text-[10px] tracking-wider text-amber-300">Registration already started</p>
              <p className="mt-1 font-body text-xs leading-relaxed text-slate-300">
                A registration with these details is already pending payment{resume.reg_no ? ` (${resume.reg_no})` : ""}.
                No need to fill the form again — just continue your payment.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                {resume.checkout_url ? (
                  <button
                    type="button"
                    onClick={() => {
                      localStorage.setItem("pahlawan_last_registration", resume.registration_id);
                      window.location.href = resume.checkout_url;
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-neon-pink px-5 py-2 font-mono-cap text-[10px] text-white transition-colors hover:bg-neon-cyan hover:text-void"
                    data-testid="resume-continue-btn"
                  >
                    Continue Payment
                  </button>
                ) : (
                  <p className="font-body text-[11px] text-slate-400">
                    Payment link unavailable — try again in about an hour, or WhatsApp Admin Pahlawan 013-332 3998.
                  </p>
                )}
                <button type="button" onClick={() => setResume(null)} className="font-mono-cap text-[10px] text-slate-400 underline underline-offset-2" data-testid="resume-dismiss">
                  Back to form
                </button>
              </div>
            </div>
          )}
          {error && (
            <p className="flex items-center gap-2 rounded-2xl border border-neon-pink/40 bg-neon-pink/10 px-5 py-3 font-body text-sm text-neon-pink" data-testid="reg-error">
              <AlertCircle className="h-4 w-4 shrink-0" /> {error}
            </p>
          )}

          {!saleOpen && (
            <p className="flex items-center gap-2 rounded-2xl border border-amber-400/40 bg-amber-400/10 px-5 py-3 font-body text-sm text-amber-300" data-testid="amateur-sale-gate">
              <Clock className="h-4 w-4 shrink-0" /> Registration opens on 22 September 2026 — mark your calendar!
            </p>
          )}

          <motion.button
            whileHover={saleOpen ? { scale: 1.01 } : undefined}
            whileTap={saleOpen ? { scale: 0.98 } : undefined}
            disabled={submitting || photo.uploading || !saleOpen}
            className="flex w-full items-center justify-center gap-3 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet py-5 font-mono-cap text-[12px] text-white disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="reg-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />}
            {saleOpen ? (submitting ? "Preparing Secure Checkout…" : `Proceed To Payment — RM${prices.amateur}`) : "Opens 22 September 2026"}
          </motion.button>
          <p className="text-center font-mono-cap text-[9px] text-slate-500">Secure payment via HitPay · FPX, cards & e-wallets accepted</p>
        </form>
      </main>
    </div>
  );
}
